<script lang="ts" setup>
import { defineAsyncComponent, onMounted, ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AccountDetails } from '@/types'
import { STATUS_CODES } from '@/constants'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { useRoute, useRouter } from 'vue-router'
import { useImpersonation } from '@/composables'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

// Lazy load tab components - only loaded when their tab is active
const AccountPageDetails = defineAsyncComponent(
  () => import('@/components/dashboard/Account/AccountPageDetails.vue')
)
const AccountPageSecurity = defineAsyncComponent(
  () => import('@/components/dashboard/Account/AccountPageSecurity.vue')
)
const AccountPagePayments = defineAsyncComponent(
  () => import('@/components/dashboard/Account/AccountPagePayments.vue')
)
const AccountPageApiTokens = defineAsyncComponent(
  () => import('@/components/dashboard/Account/AccountPageApiTokens.vue')
)
const AccountPageRoles = defineAsyncComponent(
  () => import('@/components/dashboard/Account/AccountPageRoles.vue')
)
const AccountPageSshKeys = defineAsyncComponent(
  () => import('@/components/dashboard/Account/AccountPageSshKeys.vue')
)
const AccountPageNotifications = defineAsyncComponent(
  () => import('@/components/dashboard/Account/AccountPageNotifications.vue')
)
const AccountPageConnections = defineAsyncComponent(
  () => import('@/components/dashboard/Account/AccountPageConnections.vue')
)

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { isImpersonating } = useImpersonation()

type AccountTab = 'details' | 'security' | 'payments' | 'tokens' | 'connections' | 'ssh-keys' | 'notifications' | 'roles'
const accountDetails = ref<AccountDetails>({} as AccountDetails)
const loading = ref(true)

const tab = computed<AccountTab>(() => {
  const routeTab = route.params.tab as AccountTab | undefined
  return routeTab || 'details'
})

const allTabs: Array<{ value: AccountTab; label: string }> = [
  { value: 'details', label: 'dashboard.account.tabs.details' },
  { value: 'security', label: 'dashboard.account.tabs.security' },
  { value: 'payments', label: 'dashboard.account.tabs.payments' },
  { value: 'tokens', label: 'dashboard.account.tabs.tokens' },
  { value: 'connections', label: 'dashboard.account.tabs.connections' },
  { value: 'ssh-keys', label: 'dashboard.account.tabs.sshKeys' },
  { value: 'notifications', label: 'dashboard.account.tabs.notifications' },
  { value: 'roles', label: 'dashboard.account.tabs.roles' }
]

const blockedTabsDuringImpersonation: AccountTab[] = ['security', 'payments']

const tabs = computed(() => {
  if (isImpersonating.value) {
    return allTabs.filter(item => !blockedTabsDuringImpersonation.includes(item.value))
  }
  return allTabs
})

const currentTabLabel = computed(() => {
  const current = tabs.value.find(item => item.value === tab.value)
  return current ? t(current.label) : t('dashboard.account.title')
})

watch(
  [tab, isImpersonating],
  ([currentTab, impersonating]) => {
    if (impersonating && blockedTabsDuringImpersonation.includes(currentTab)) {
      router.replace({ name: 'dashboardAccount', params: { tab: 'details' } })
    }
  },
  { immediate: true }
)

const getAccountDetails = () => {
  loading.value = true
  useAxios<null, AccountDetails>('GET', '/account', true)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        const validate = AccountDetails.safeParse(res.data)
        if (!validate.success) return
        accountDetails.value = { ...validate.data }
      }
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'DashboardAccount' })
    })
    .finally(() => {
      loading.value = false
    })
}

onMounted(() => {
  getAccountDetails()
})
</script>

<template>
  <BaseDashboardPage
    :title="currentTabLabel"
    icon="mdi-account-cog"
    icon-color="cyan"
  >
    <!-- Content Area -->
    <div class="content-area">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state dashboard-loading-state">
        <v-progress-circular indeterminate color="primary" size="48" />
        <p>{{ $t('dashboard.account.loadingAccountData') }}</p>
      </div>

      <!-- Tab Content -->
      <Transition name="fade" mode="out-in">
        <div v-if="!loading" :key="tab ?? 'default'">
          <AccountPageDetails
            v-if="tab === 'details'"
            :account-details="accountDetails"
          />
          <AccountPageSecurity
            v-else-if="tab === 'security'"
            :account-details="accountDetails"
          />
          <AccountPagePayments
            v-else-if="tab === 'payments'"
          />
          <AccountPageApiTokens
            v-else-if="tab === 'tokens'"
          />
          <AccountPageConnections
            v-else-if="tab === 'connections'"
          />
          <AccountPageSshKeys
            v-else-if="tab === 'ssh-keys'"
          />
          <AccountPageNotifications
            v-else-if="tab === 'notifications'"
          />
          <AccountPageRoles
            v-else-if="tab === 'roles'"
          />
        </div>
      </Transition>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Content Area */
.content-area {
  background: rgba(15, 23, 48, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: var(--dashboard-card-radius);
  padding: var(--dashboard-section-margin);
  min-height: 300px;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  color: rgba(200, 218, 255, 0.6);

  p {
    font-size: 0.9rem;
    margin: 0;
  }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Light Theme */
.v-theme--lightTheme {
  .content-area {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .content-area {
    padding: 20px;
    border-radius: 16px;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}
</style>
